import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import { persistQueryClient } from '@tanstack/query-persist-client-core'
import { environment } from '@/constants/environment'

// Create query cache with error handling
const queryCache = new QueryCache({
  onError: (error, query) => {
    console.error('❌ Query Error:', {
      queryKey: query.queryKey,
      error,
    })

    // Handle specific error types from our API
    if (error && typeof error === 'object' && 'type' in error) {
      switch (error.type) {
        case 'network':
          console.warn('Network error in query:', query.queryKey)
          break
        case 'api':
          if ('status' in error && error.status === 401) {
            console.warn('Unauthorized query:', query.queryKey)
          }
          break
      }
    }
  },
})

// Create mutation cache with error handling
const mutationCache = new MutationCache({
  onError: (error, variables, _context, mutation) => {
    console.error('❌ Mutation Error:', {
      mutationKey: mutation.options.mutationKey,
      variables,
      error,
    })
  },
})

// Create and configure QueryClient
export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      // Stale time - how long data is considered fresh
      staleTime: 5 * 60 * 1000, // 5 minutes

      // Cache time - how long inactive data stays in cache
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)

      // Retry configuration
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (except 401 which is handled by interceptor)
        if (error && typeof error === 'object' && 'status' in error) {
          const status = error.status as number
          if (status >= 400 && status < 500 && status !== 401) {
            return false
          }
        }

        // Don't retry cancelled requests
        if (error && typeof error === 'object' && 'type' in error && error.type === 'cancelled') {
          return false
        }

        // Retry up to 3 times with exponential backoff
        return failureCount < 3
      },

      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch configuration
      refetchOnWindowFocus: false, // Don't refetch when window gains focus
      refetchOnMount: true, // Refetch when component mounts
      refetchOnReconnect: true, // Refetch when network reconnects

      // Network mode
      networkMode: 'online', // Only run queries when online
    },
    mutations: {
      // Retry mutations less aggressively
      retry: (failureCount, error) => {
        // Don't retry on client errors
        if (error && typeof error === 'object' && 'status' in error) {
          const status = error.status as number
          if (status >= 400 && status < 500) {
            return false
          }
        }

        return failureCount < 1 // Only retry once for mutations
      },

      // Network mode for mutations
      networkMode: 'online',
    },
  },
})

// Query persistence setup (if enabled)
if (environment.features.enableQueryPersist) {
  const persistOptions = {
    queryClient,
    persister: {
      persistClient: async (client: any) => {
        try {
          localStorage.setItem('react-query-cache', JSON.stringify(client))
        } catch (error) {}
      },
      restoreClient: async () => {
        try {
          const cached = localStorage.getItem('react-query-cache')
          return cached ? JSON.parse(cached) : undefined
        } catch (error) {
          return undefined
        }
      },
      removeClient: async () => {
        try {
          localStorage.removeItem('react-query-cache')
        } catch (error) {}
      },
    },
    maxAge: 1000 * 60 * 60 * 24, // 24 hours
    hydrateOptions: {},
    dehydrateOptions: {
      shouldDehydrateQuery: (query: any) => {
        // Only persist successful queries
        return query.state.status === 'success'
      },
    },
  }

  // Set up persistence asynchronously
  ;(async () => {
    try {
      await persistQueryClient(persistOptions)
    } catch (error: any) {}
  })()
}

// Cleanup function for when app unmounts or user logs out
export const cleanupQueryClient = (): void => {
  queryClient.clear()

  if (environment.features.enableQueryPersist) {
    try {
      localStorage.removeItem('react-query-cache')
    } catch (error) {}
  }
}

// Query key factory for consistent key management
export const queryKeys = {
  // Auth
  auth: ['auth'] as const,
  authProfile: () => [...queryKeys.auth, 'profile'] as const,

  // Users
  users: ['users'] as const,
  userProfile: (userId?: string) => [...queryKeys.users, 'profile', userId] as const,
  userList: (filters?: any) => [...queryKeys.users, 'list', filters] as const,

  // Session
  session: ['session'] as const,
  sessionCurrent: () => [...queryKeys.session, 'current'] as const,

  // Customer
  customers: ['customers'] as const,
  customer: (customerId: string) => [...queryKeys.customers, customerId] as const,
  customerList: (filters?: any) => [...queryKeys.customers, 'list', filters] as const,
  customerAddresses: () => [...queryKeys.customers, 'addresses'] as const,

  // Cart
  cart: ['cart'] as const,
  cartCurrent: () => [...queryKeys.cart, 'current'] as const,
  cartItems: () => [...queryKeys.cart, 'items'] as const,

  // Products
  products: ['products'] as const,
  product: (productId: string) => [...queryKeys.products, productId] as const,
  productList: (filters?: any) => [...queryKeys.products, 'list', filters] as const,

  // Payment Sources
  paymentSources: ['paymentSources'] as const,
  paymentSourcesList: () => [...queryKeys.paymentSources, 'list'] as const,

  // Orders
  orders: ['orders'] as const,
  order: (orderId: string) => [...queryKeys.orders, orderId] as const,
  orderList: (filters?: any) => [...queryKeys.orders, 'list', filters] as const,

  // Order Management Portal — order_items back subscriptions and orders
  portal: ['portal'] as const,
  portalOrderItems: (filters?: any) => [...queryKeys.portal, 'order-items', filters] as const,
  portalItem: (itemId: string | number) => [...queryKeys.portal, 'item', itemId] as const,
} as const

// Listen for auth logout event to cleanup queries
if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    cleanupQueryClient()
  })
}

export default queryClient
